import React, { useId, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, LifeBuoy, MessageSquareText } from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { cn } from "../../lib/utils";
import { track } from "../../lib/analytics";

const ANSWER_ENTER = {
  duration: 0.18,
  ease: [0.165, 0.84, 0.44, 1],
};

// PGC-9: safe inline links for FAQ answers. An item may carry its own `link: {href, label}`
// (the parts2.js fallback does); CMS-served items carry only {id,q,a} — the seeded ids that
// reference another page resolve here, so the answer that says "the pricing page shows…"
// finally lets the reader click through. Internal routes only — never a free-text URL.
const SAFE_FAQ_LINKS = Object.freeze({
  f2: { href: "/pricing", label: "See plans and full pricing" },
  // TRUST-8: f10 says support and onboarding are "confirmed for each school in its plan or
  // proposal" — true, but a reader still needs to see the channels that exist today. /support
  // publishes them (three labelled phone lines, WhatsApp, sales/support inboxes, the escalation
  // path and what onboarding covers) and deliberately states NO response-time guarantee, so this
  // link adds reachability without adding a commitment.
  f10: { href: "/support", label: "See support channels and escalation" },
});

function resolveFaqLink(item) {
  const link = item?.link && item.link.href ? item.link : SAFE_FAQ_LINKS[item?.id];
  if (!link) return null;
  const href = String(link.href || "");
  // Router-internal destinations only (a stored CMS row can never inject an external URL).
  if (!href.startsWith("/") || href.startsWith("//")) return null;
  return { href, label: String(link.label || "Learn more") };
}

function FaqRow({ answer, index, isOpen, link, onToggle, question }) {
  const uid = useId();
  const buttonId = `faq-trigger-${uid}`;
  const panelId = `faq-panel-${uid}`;
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduce = prefersReducedMotion || paused;

  return (
    <li className="group border-t border-line first:border-t-0" data-testid="faq-item">
      <h3 className="m-0">
        <button
          id={buttonId}
          type="button"
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="grid min-h-[5.25rem] w-full grid-cols-[2.25rem_1fr_auto] items-center gap-3 rounded-lg py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-4 focus-visible:ring-offset-surface sm:grid-cols-[2.75rem_1fr_auto] sm:gap-5 sm:py-6"
        >
          <span className={cn(
            "font-mono text-[11px] font-semibold tracking-[0.12em] transition-colors duration-fast",
            isOpen ? "text-accent" : "text-ink-subtle group-hover:text-ink-2",
          )} aria-hidden="true">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="max-w-3xl font-heading text-[17px] font-semibold leading-snug tracking-[-0.012em] text-ink sm:text-[19px]">
            <BrandCopy>{question}</BrandCopy>
          </span>
          <span className={cn(
            "grid h-9 w-9 shrink-0 place-items-center rounded-full border transition-[background-color,border-color,color,transform] duration-base ease-premium",
            isOpen
              ? "rotate-180 border-accent bg-accent text-accent-fg"
              : "border-line bg-surface text-ink-2 group-hover:border-accent/45 group-hover:text-accent",
          )}>
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </span>
        </button>
      </h3>

      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!isOpen}
        className="pb-6 pl-[3.25rem] pr-12 sm:pb-7 sm:pl-[4rem] sm:pr-16"
      >
        <m.div
          initial={reduce ? false : { opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduce ? { duration: 0 } : ANSWER_ENTER}
        >
          <p className="max-w-[68ch] whitespace-pre-line text-[15px] leading-7 text-ink-2 [text-wrap:pretty]">
            <BrandCopy>{answer}</BrandCopy>
          </p>
          {link && (
            <Link
              to={link.href}
              className="mt-3 inline-flex min-h-10 items-center gap-1.5 rounded text-sm font-semibold text-accent-active hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring"
              data-testid="faq-answer-link"
            >
              {link.label} <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
        </m.div>
      </div>
    </li>
  );
}

export default function PremiumFaqSection({ faq = [], onBookReview }) {
  const [openIndex, setOpenIndex] = useState(faq.length ? 0 : -1);
  const structuredFaq = faq.filter((item) => item?.q && item?.a);

  const toggle = (index, question) => {
    const opening = openIndex !== index;
    if (opening) track("faq_open", { faq_question: String(question).slice(0, 80) });
    setOpenIndex(opening ? index : -1);
  };

  return (
    <section id="faq" className="relative isolate scroll-mt-24 overflow-hidden border-y border-line bg-surface" aria-labelledby="faq-heading">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_14%_18%,color-mix(in_srgb,var(--vp-accent)_11%,transparent),transparent_31%)]" aria-hidden="true" />
      <div className="mx-auto grid max-w-6xl lg:grid-cols-[0.78fr_1.22fr]">
        <header className="border-b border-line px-5 py-14 sm:px-8 sm:py-16 lg:border-b-0 lg:border-r lg:px-10 lg:py-20">
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-accent">
              <MessageSquareText className="h-4 w-4" aria-hidden="true" />
              Decision-ready answers
            </div>
            <h2 id="faq-heading" className="mt-5 max-w-lg font-heading text-[clamp(2rem,4vw,3.25rem)] font-bold leading-[1.06] tracking-[-0.038em] text-ink [text-wrap:balance]">
              Before you bring it to the school committee.
            </h2>
            <p className="mt-5 max-w-md text-[16px] leading-7 text-ink-2 [text-wrap:pretty]">
              Clear answers to the questions school leaders ask about scope, rollout, payments, data, privacy and AI.
            </p>

            <div className="mt-10 border-l-2 border-accent pl-5">
              <p className="font-heading text-base font-semibold text-ink">Your school has an exception?</p>
              <p className="mt-1.5 max-w-sm text-sm leading-6 text-ink-2">
                Bring one real workflow. We will review its people, permissions, providers and approval gates with you.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
                <button
                  type="button"
                  onClick={onBookReview}
                  className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-accent-fg shadow-[0_10px_25px_-12px_color-mix(in_srgb,var(--vp-accent)_70%,transparent)] transition-[background-color,transform] duration-fast ease-premium hover:bg-accent-hover active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                  data-testid="faq-book-review"
                >
                  Book a workflow demo
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <Link
                  to="/help"
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg text-sm font-semibold text-ink-2 transition-colors duration-fast hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring"
                >
                  <LifeBuoy className="h-4 w-4" aria-hidden="true" />
                  Browse Help Center
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-14">
          <div className="flex items-center justify-between gap-5 border-b border-line pb-5">
            <p className="font-heading text-sm font-semibold text-ink">Evaluation FAQ</p>
            <p className="text-xs font-medium tabular-nums text-ink-subtle">
              {structuredFaq.length} {structuredFaq.length === 1 ? "answer" : "answers"}
            </p>
          </div>

          <ol data-testid="faq-list">
            {structuredFaq.map((item, index) => (
              <FaqRow
                key={item.id || item.q}
                index={index}
                question={item.q}
                answer={item.a}
                link={resolveFaqLink(item)}
                isOpen={openIndex === index}
                onToggle={() => toggle(index, item.q)}
              />
            ))}
          </ol>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: structuredFaq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }).replace(/</g, "\\u003c"),
        }}
      />
    </section>
  );
}
